import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MessageCircle, RefreshCw, Check } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { useDiagnosticStore, useAuthStore } from '@shared/state';
import { saveProfile } from '@shared/services/profile';
import { recordGamificationAction } from '@shared/services/gamification';
import { generateProfileSummary } from '@shared/services/profile-summary';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { DiagnosticConversation } from '../components/diagnostic/DiagnosticConversation';
import { DiagnosticProgress } from '../components/diagnostic/DiagnosticProgress';
import { ProfileReportCard } from '../components/diagnostic/ProfileReportCard';
import { LoadingFacts } from '../components/ui/LoadingFacts';
import type { DiagnosticMode } from '@shared/types/diagnostic';
import type { CogniPrintProfile, DiagnosticDimensionKey } from '@shared/types/cogniprint';

export function DiagnosticPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const store = useDiagnosticStore();
  const [reportProfile, setReportProfile] = useState<CogniPrintProfile | null>(store.profile);
  const [chatOpen, setChatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [disputing, setDisputing] = useState<DiagnosticDimensionKey | null>(null);
  const [rewording, setRewording] = useState(false);

  const mode = (searchParams.get('mode') as DiagnosticMode) || 'quick';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (store.status === 'not_started') {
      store.startDiagnostic(mode);
    }
  }, [user, mode, store, navigate]);

  // A new complete profile arrived — first completion, a dispute re-score, or a rewrite.
  function handleReportReady(profile: CogniPrintProfile) {
    setReportProfile(profile);
    setDisputing(null);
  }

  function handleDispute(dimension: DiagnosticDimensionKey) {
    setDisputing(dimension);
    setChatOpen(true);
    store.requestDispute(dimension, "This doesn't feel right to me.");
  }

  /** Wording only. The scores the narrator sees are the same scores. */
  async function handleReword() {
    if (!user || !reportProfile || rewording) return;
    setRewording(true);
    try {
      const summary = await generateProfileSummary(reportProfile, user.displayName || 'learner', 'Please say this differently.');
      const next = { ...reportProfile, summary };
      setReportProfile(next);
      store.setProfile(next);
      store.setStatus('in_progress');
    } catch {
      setSaveError("Couldn't reword the summary just now. The results are unchanged.");
    } finally {
      setRewording(false);
    }
  }

  /**
   * THE save. Nothing reaches cogniprints/{uid} until the learner presses this.
   * It used to save automatically the moment the profile was computed, with
   * "Confirm & Save" merely navigating — which meant a profile the learner had not
   * looked at was already on their account.
   */
  async function handleSave() {
    if (!user || !reportProfile || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveProfile(user.uid, reportProfile);
      const tier = user.subscription;
      void recordGamificationAction(user.uid, 'diagnostic_completed', {
        counterMutations: { diagnosticCompleted: true },
        tickStreak: true,
        tierAllowsFreezes: tier === 'pro' || tier === 'teams',
      });
      if (store.sessionId) {
        await updateDoc(doc(db, 'diagnosticSessions', store.sessionId), {
          status: 'confirmed',
          confirmedAt: new Date().toISOString(),
          profile: reportProfile,
        }).catch(console.error);
      }
      navigate('/profile/reveal');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save your profile. Please try again.");
      setSaving(false);
    }
  }

  const closingLine = [...store.messages].reverse().find((m) => m.role === 'assistant' && !m.options?.length)?.content;
  const isFree = user?.subscription === 'free';
  const inReview = !!reportProfile;

  return (
    <div className="min-h-screen bg-dark-bg relative">
      <RidgeTexture opacity="opacity-[0.025]" />

      <div className="relative z-10 px-4 pt-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-content hover:text-white transition-colors font-body font-medium">
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </div>

      {store.status === 'computing' ? (
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <motion.div className="text-center relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.img
              src="/ThumbsUpCog.png" alt="Professor Cogni"
              className="w-20 h-20 mx-auto mb-4 object-contain"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <p className="text-content-secondary font-body text-sm mb-2">Building your CogniPrint profile…</p>
            <p className="text-xs text-content-dim font-body mb-4">This usually takes 10–20 seconds</p>
            <LoadingFacts />
          </motion.div>
        </div>
      ) : !inReview ? (
        /* ── During: the conversation, full width, progress always visible ── */
        <div className="relative z-10 flex items-start justify-center px-4 py-4" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <div
            className="flex flex-col rounded-2xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden w-full"
            style={{ height: '88vh', minHeight: 500, maxWidth: 950 }}
          >
            <div className="shrink-0 px-5 py-4 border-b border-dark-border bg-dark-surface flex items-center gap-3">
              <img src="/CogHead.png" alt="Professor Cogni" className="w-10 h-10 object-contain" />
              <div>
                <p className="font-heading font-bold text-base text-content">Professor Cogni</p>
                <p className="text-sm text-content-secondary font-body">
                  {mode === 'quick' ? 'Quick Diagnostic' : 'Full Diagnostic'} · CogniPrint
                </p>
              </div>
            </div>
            <DiagnosticProgress />
            <DiagnosticConversation onReportReady={handleReportReady} />
          </div>
        </div>
      ) : (
        /* ── After: the report is the page. Chat returns only when asked for. ── */
        <div className="relative z-10 flex items-start justify-center gap-4 px-4 py-4" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <motion.div
            // Explicit, not `layout`: a layout-only motion.div mounted here rendered
            // at opacity 0 and stayed there (2026-09-05 run). Say what we mean.
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0, maxWidth: chatOpen ? 720 : 860 }}
            className="flex flex-col rounded-2xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden w-full"
            style={{ height: '88vh', minHeight: 500 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
          >
            {/* Cogni's closing line */}
            {closingLine && (
              <div className="shrink-0 px-6 py-4 border-b border-dark-border flex items-start gap-3">
                <img src="/CogHead.png" alt="Professor Cogni" className="w-9 h-9 object-contain shrink-0" />
                <p className="text-sm text-content-secondary font-body leading-relaxed">{closingLine}</p>
              </div>
            )}

            <div className="flex-1 overflow-hidden">
              <ProfileReportCard
                profile={reportProfile}
                isFree={isFree}
                onDispute={handleDispute}
                disputing={disputing}
                disputed={store.disputed}
              />
            </div>

            {/* Actions */}
            <div className="shrink-0 border-t border-dark-border bg-dark-surface px-6 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !!disputing}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gold-500 text-dark-bg font-body font-semibold text-sm
                    hover:bg-gold-400 transition-colors cursor-pointer shadow-glow-gold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Check size={16} />
                  {saving ? 'Saving…' : 'Save my CogniPrint'}
                </button>
                <button
                  type="button"
                  onClick={() => setChatOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dark-border text-sm text-content font-body
                    hover:border-indigo-500/50 hover:bg-dark-hover transition-colors cursor-pointer"
                >
                  <MessageCircle size={15} />
                  {chatOpen ? 'Hide the chat' : "Something's off"}
                </button>
                <button
                  type="button"
                  onClick={handleReword}
                  disabled={rewording || saving}
                  className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-content-muted font-body hover:text-content transition-colors cursor-pointer disabled:opacity-60"
                >
                  <RefreshCw size={14} className={rewording ? 'animate-spin' : ''} />
                  {rewording ? 'Rewording…' : 'Reword the summary'}
                </button>
              </div>
              {saveError && <p className="text-error text-xs font-body mt-2">{saveError}</p>}
              {/* Decision note — JB's to overturn. */}
              <p className="text-[11px] text-content-dim font-body mt-3 leading-relaxed">
                Nothing is saved until you press Save. If a result doesn't feel right, Professor Cogni will ask you one more
                question about it and re-score from your answer — results come from what you tell him, never from a slider.
                The write-up can be reworded as often as you like; the results underneath it don't change.
              </p>
            </div>
          </motion.div>

          {/* Side chat: for disputes and rewording */}
          <AnimatePresence>
            {chatOpen && (
              <motion.div
                initial={{ opacity: 0, x: 40, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 'min(420px, 38vw)' }}
                exit={{ opacity: 0, x: 40, width: 0 }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
                className="hidden lg:flex flex-col rounded-2xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden"
                style={{ height: '88vh', minHeight: 500 }}
              >
                <div className="shrink-0 px-4 py-3 border-b border-dark-border flex items-center gap-2.5">
                  <img src="/CogHead.png" alt="Professor Cogni" className="w-7 h-7 object-contain" />
                  <div>
                    <p className="font-heading font-semibold text-sm">Talk it through</p>
                    <p className="text-[11px] text-content-muted font-body">Say which part feels wrong, or ask for a reword</p>
                  </div>
                </div>
                <DiagnosticConversation onReportReady={handleReportReady} variant="side" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
